import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // Send the admin session cookie (Emergent Google Auth) alongside requests.
  withCredentials: true,
});

// ---- Emergent Google Auth (open signup; admin = allowlist on top) ----
// Exchange the one-time session_id (from the redirect URL fragment) for a
// site-user session cookie. ANY Google account is admitted.
export const googleAuthSession = (sessionId) =>
  api
    .post("/auth/google/session", null, { headers: { "X-Session-ID": sessionId } })
    .then((r) => r.data);

export const authMe = () => api.get("/auth/me").then((r) => r.data);

export const authLogout = () => api.post("/auth/logout").then((r) => r.data);

// Kick off the Emergent Google sign-in. `returnPath` is where the user lands
// back on this site after auth (must be a real route on this origin).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
export const startGoogleLogin = (returnPath = "/") => {
  const redirectUrl = window.location.origin + returnPath;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// ---- Auth-user cache (shared, single fetch) ----
// undefined = not yet resolved; null = anonymous; object = logged-in user.
let _authUser;
let _authUserPromise;
export const loadAuthUser = () => {
  // CRITICAL (Emergent Auth race fix): when returning from the OAuth callback
  // the URL still carries `#session_id=...` and the session cookie has NOT been
  // set yet. Skip the /auth/me check (and do NOT cache the result) so we don't
  // poison the shared cache with an anonymous result before AuthCallback
  // exchanges the session_id. AuthCallback hard-reloads once the cookie is set.
  if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
    return Promise.resolve(null);
  }
  if (_authUserPromise) return _authUserPromise;
  _authUserPromise = authMe()
    .then((u) => {
      _authUser = u && u.email ? u : null;
      return _authUser;
    })
    .catch(() => {
      _authUser = null;
      return null;
    });
  return _authUserPromise;
};
export const getAuthUserSync = () => _authUser;
// Drop the cached identity (after logout) so the next read re-fetches.
export const resetAuthUser = () => {
  _authUser = undefined;
  _authUserPromise = undefined;
};

// ---- "Build for agent" export from raw slugs (no saved terminal) ----
export const agentExportJson = (slugs) =>
  fetch(`${API}/agent-export?format=json`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slugs }),
  }).then((r) => r.json());

export const downloadAgentExport = async (slugs, format) => {
  const res = await fetch(`${API}/agent-export?format=${format}&download=1`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slugs }),
  });
  if (!res.ok) throw new Error("export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = format === "md" ? "stack.md" : format === "readme" ? "AGENT-README.md" : "stack.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ---- Per-user "most used" tools ----
// Fire-and-forget; only records for logged-in users (no-op otherwise so we
// don't spam 401s for anonymous visitors).
export const recordLaunch = (toolId) => {
  if (!_authUser || !toolId) return;
  api.post("/usage/launch", { tool_id: toolId }).catch(() => {});
};
export const getUsageTop = (limit = 6) =>
  api.get("/usage/top", { params: { limit } }).then((r) => r.data);


export const ADMIN_TOKEN_KEY = "tv_admin_token";

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}
export function setAdminToken(t) {
  localStorage.setItem(ADMIN_TOKEN_KEY, t);
}
export function clearAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function adminHeaders() {
  return { "X-Admin-Password": getAdminToken() };
}

// Public
export const fetchTools = ({ category, assetClass, ticker, tag, delivery, isOpenSource, sector, userBase } = {}) => {
  const params = {};
  if (category && category !== "all") params.category = category;
  if (assetClass && assetClass !== "all") params.asset_class = assetClass;
  if (ticker && ticker !== "all") params.ticker = ticker;
  if (tag && tag !== "all") params.tag = tag;
  if (delivery && delivery !== "all") params.delivery = delivery;
  if (sector && sector !== "all") params.sector = sector;
  if (userBase && userBase !== "all") params.user_base = userBase;
  if (isOpenSource) params.is_open_source = "true";
  return api.get("/tools", { params }).then((r) => r.data.tools);
};

export const fetchTickers = () =>
  api.get("/tickers").then((r) => r.data.tickers);

export const fetchTags = () => api.get("/tags").then((r) => r.data.tags);

export const fetchCategories = () =>
  api.get("/categories").then((r) => r.data);

export const submitTool = (payload) => api.post("/tools", payload).then((r) => r.data);

export const upvoteTool = (id) => api.post(`/tools/${id}/upvote`).then((r) => r.data);

// Prospect requests a demo via a claimed listing's built-in form
export const submitVendorLead = (payload) =>
  api.post("/vendor/lead", payload).then((r) => r.data);

// Shareable stacks
export const createStack = ({ tool_ids, title }) =>
  api.post("/stacks", { tool_ids, title }).then((r) => r.data);
export const fetchStack = (sid) =>
  api.get(`/stacks/${sid}`).then((r) => r.data);

export const fetchVoted = () => api.get("/tools/voted").then((r) => r.data.tool_ids);

// Personal Terminal ("command deck") — built from the tools the AI concierge
// recommended in a session (via the "build for human" offer).
export const createTerminal = ({ slugs, tool_ids, title }) =>
  api.post("/terminals", { slugs, tool_ids, title }).then((r) => r.data);
export const fetchTerminal = (id) =>
  api.get(`/terminals/${id}`).then((r) => r.data);
export const addTerminalTools = (id, slugs) =>
  api.post(`/terminals/${id}/tools`, { slugs }).then((r) => r.data);
// The signed-in user's stacks (newest first) for the persistent switcher bar.
export const getMyTerminals = () =>
  api.get("/terminals/mine").then((r) => r.data.terminals);
// Owner-only edits: rename + reorder, and remove a single tool.
export const updateTerminal = (id, body) =>
  api.patch(`/terminals/${id}`, body).then((r) => r.data);
export const removeTerminalTool = (id, toolId) =>
  api.delete(`/terminals/${id}/tools/${toolId}`).then((r) => r.data);

// Personal Workspace — account-tied layout of specific tools (one per window),
// auto-launchable as tiled windows from the installed PWA.
export const getWorkspace = () => api.get("/workspace").then((r) => r.data);
export const saveWorkspace = ({ slugs, name }) =>
  api.put("/workspace", { slugs, name }).then((r) => r.data);
export const deleteWorkspace = () => api.delete("/workspace").then((r) => r.data);

// Admin
export const adminLogin = (password) => api.post("/admin/login", { password }).then((r) => r.data);

export const adminListTools = (status) =>
  api.get("/admin/tools", { params: status ? { status } : {}, headers: adminHeaders() }).then((r) => r.data.tools);

export const adminSetStatus = (id, status) =>
  api.post(`/admin/tools/${id}/status`, { status }, { headers: adminHeaders() }).then((r) => r.data);

export const adminDelete = (id) =>
  api.delete(`/admin/tools/${id}`, { headers: adminHeaders() }).then((r) => r.data);

export const adminUpdate = (id, patch) =>
  api.patch(`/admin/tools/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data);

export const adminToolHandles = (q = "", onlyMissing = false) =>
  api
    .get("/admin/tool-handles", { headers: adminHeaders(), params: { q, only_missing: onlyMissing }, timeout: 15000 })
    .then((r) => r.data);

export const adminToolStrategies = (q = "", onlyMissing = false) =>
  api
    .get("/admin/tool-strategies", { headers: adminHeaders(), params: { q, only_missing: onlyMissing }, timeout: 15000 })
    .then((r) => r.data);

export const adminStrategiesSuggest = ({ slugs, onlyMissing = true, limit = 25 } = {}) =>
  api
    .post(
      "/admin/strategies/suggest",
      { slugs: slugs || null, only_missing: onlyMissing, limit },
      { headers: adminHeaders(), timeout: 120000 },
    )
    .then((r) => r.data);

export const adminStrategiesVocab = () =>
  api.get("/admin/strategies", { headers: adminHeaders() }).then((r) => r.data);

export const adminAutoTagAll = () =>
  api.post("/admin/strategies/auto-tag-all", {}, { headers: adminHeaders() }).then((r) => r.data);

export const adminAutoTagAllStatus = () =>
  api.get("/admin/strategies/auto-tag-all/status", { headers: adminHeaders() }).then((r) => r.data);

export const adminRebuildStatic = () =>
  api.post("/admin/rebuild-static", {}, { headers: adminHeaders() }).then((r) => r.data);

export const adminSeoImportSearchDemand = (csv) =>
  api
    .post("/admin/seo/search-demand/import", { csv }, { headers: adminHeaders(), timeout: 120000 })
    .then((r) => r.data);

export const adminSeoSearchDemandReport = () =>
  api.get("/admin/seo/search-demand/report", { headers: adminHeaders(), timeout: 30000 }).then((r) => r.data);

export const adminSeoDuplicates = () =>
  api.get("/admin/seo/duplicates", { headers: adminHeaders() }).then((r) => r.data);

export const adminSeoFix = (slugs) =>
  api.post("/admin/seo/fix", { slugs }, { headers: adminHeaders() }).then((r) => r.data);

export const adminSeoFixStatus = () =>
  api.get("/admin/seo/fix/status", { headers: adminHeaders() }).then((r) => r.data);

// ---- Vendor plans (claim / Starter) ----
export const vendorFlags = () => api.get("/vendor/flags", { headers: adminHeaders() }).then((r) => r.data);
export const vendorMe = () => api.get("/vendor/me").then((r) => r.data);
export const vendorClaimStart = (slug) => api.post("/vendor/claim/start", { slug }).then((r) => r.data);
export const vendorClaimVerify = (slug) => api.post("/vendor/claim/verify", { slug }).then((r) => r.data);
export const vendorClaimRelease = (slug) => api.post("/vendor/claim/release", { slug }).then((r) => r.data);
export const vendorCorrection = (slug, message) => api.post("/vendor/correction", { slug, message }).then((r) => r.data);
export const vendorCheckout = (slug, lookup_key) =>
  api.post("/vendor/checkout", { slug, lookup_key, origin_url: window.location.origin }).then((r) => r.data);
export const vendorPaymentStatus = (sessionId) => api.get(`/vendor/payments/status/${sessionId}`).then((r) => r.data);
export const vendorPortal = (slug) =>
  api.post("/vendor/portal", { slug, origin_url: window.location.origin }).then((r) => r.data);
export const vendorSaveProfile = (body) => api.put("/vendor/profile", body).then((r) => r.data);
export const vendorUploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/vendor/upload", fd).then((r) => r.data);
};
export const vendorCaptionSuggest = (url, title) =>
  api.post("/vendor/caption-suggest", { url, title }, { timeout: 30000 }).then((r) => r.data);
export const vendorBadges = (slug) => api.get(`/vendor/badges/${slug}`).then((r) => r.data);
export const vendorSponsorOptions = (slug) => api.get(`/vendor/sponsor/options/${slug}`).then((r) => r.data);
export const vendorSponsorCheckout = (slug, placement, months) =>
  api.post("/vendor/sponsor/checkout", { slug, placement, months, origin_url: window.location.origin }).then((r) => r.data);
export const adminSponsored = () => api.get("/admin/sponsored", { headers: adminHeaders() }).then((r) => r.data);
export const adminMonetization = () => api.get("/admin/monetization", { headers: adminHeaders() }).then((r) => r.data);
export const adminVendorActivity = () => api.get("/admin/vendor/activity", { headers: adminHeaders() }).then((r) => r.data);
export const adminCheckoutPreview = (lookup_key) =>
  api.post("/vendor/checkout/admin-preview", { lookup_key, origin_url: window.location.origin }, { headers: adminHeaders() }).then((r) => r.data);
export const adminPreviewVendorSession = () =>
  api.post("/admin/preview/vendor-session", {}, { headers: adminHeaders() }).then((r) => r.data);

export const ownerCampaignRecipients = () =>
  api.get("/admin/owner-campaign/recipients", { headers: adminHeaders() }).then((r) => r.data);

export const ownerCampaignTest = (email) =>
  api.post("/admin/owner-campaign/test", { email }, { headers: adminHeaders() }).then((r) => r.data);

export const approvalEmailTest = (email) =>
  api.post("/admin/approval-email/test", { email }, { headers: adminHeaders() }).then((r) => r.data);

export const ownerCampaignSend = (limit = 40) =>
  api.post("/admin/owner-campaign/send", { limit }, { headers: adminHeaders() }).then((r) => r.data);

export const ownerCampaignSendSlugs = (slugs) =>
  api.post("/admin/owner-campaign/send", { slugs }, { headers: adminHeaders(), timeout: 120000 }).then((r) => r.data);
export const adminOutreach = () => api.get("/admin/outreach", { headers: adminHeaders() }).then((r) => r.data);
export const adminOutreachPreview = (slug, body) => api.post(`/admin/outreach/${slug}/preview`, body, { headers: adminHeaders() }).then((r) => r.data);
export const adminOutreachInvite = (slug, body) => api.post(`/admin/outreach/${slug}/invite`, body, { headers: adminHeaders(), timeout: 45000 }).then((r) => r.data);
export const adminOutreachStatus = (slug, body) => api.post(`/admin/outreach/${slug}/status`, body, { headers: adminHeaders() }).then((r) => r.data);
export const adminOutreachPitchPreview = (slug, body) => api.post(`/admin/outreach/${slug}/pitch/preview`, body, { headers: adminHeaders() }).then((r) => r.data);
export const adminOutreachPitch = (slug, body) => api.post(`/admin/outreach/${slug}/pitch`, body, { headers: adminHeaders(), timeout: 45000 }).then((r) => r.data);
export const adminOutreachRunNudges = () => api.post("/admin/outreach/nudges/run", {}, { headers: adminHeaders(), timeout: 120000 }).then((r) => r.data);
export const adminOutreachUpsellPreview = (slug, body) => api.post(`/admin/outreach/${slug}/upsell/preview`, body, { headers: adminHeaders() }).then((r) => r.data);
export const adminOutreachUpsell = (slug, body) => api.post(`/admin/outreach/${slug}/upsell`, body, { headers: adminHeaders(), timeout: 45000 }).then((r) => r.data);
export const adminDigestPreview = () => api.get("/admin/outreach/digest/preview", { headers: adminHeaders() }).then((r) => r.data);
export const adminDigestSend = () => api.post("/admin/outreach/digest/send", {}, { headers: adminHeaders(), timeout: 45000 }).then((r) => r.data);
export const adminSponsoredRatecard = () => api.get("/admin/sponsored/ratecard", { headers: adminHeaders() }).then((r) => r.data);
export const claimInviteInfo = (token) => api.get(`/claim/invite/${token}`).then((r) => r.data);
export const vendorClaimInvite = (token) => api.post("/vendor/claim/invite", { token }).then((r) => r.data);
export const adminSponsoredCreate = (body) => api.post("/admin/sponsored", body, { headers: adminHeaders() }).then((r) => r.data);
export const adminSponsoredCancel = (id) => api.delete(`/admin/sponsored/${id}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminSponsoredPricing = (body) => api.put("/admin/sponsored/pricing", body, { headers: adminHeaders() }).then((r) => r.data);
export const adminVendorFlags = () => api.get("/admin/vendor/flags", { headers: adminHeaders() }).then((r) => r.data);
export const adminSetVendorFlags = (flags) =>
  api.put("/admin/vendor/flags", flags, { headers: adminHeaders() }).then((r) => r.data);
export const adminVendorOverview = () => api.get("/admin/vendor/overview", { headers: adminHeaders() }).then((r) => r.data);
export const adminVendorReview = (slug, action, note) =>
  api.post(`/admin/vendor/profile/${slug}/review`, { action, note }, { headers: adminHeaders() }).then((r) => r.data);
export const adminVendorUnclaim = (slug) =>
  api.post(`/admin/vendor/unclaim/${slug}`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminPreviewEmail = (type, email) =>
  api.post("/admin/vendor/preview-email", { type, email }, { headers: adminHeaders() }).then((r) => r.data);

export const adminSeoMerge = (keep_slug, remove_slug) =>
  api.post("/admin/seo/merge", { keep_slug, remove_slug }, { headers: adminHeaders() }).then((r) => r.data);

export const adminRebuildStaticStatus = () =>
  api.get("/admin/rebuild-static/status", { headers: adminHeaders() }).then((r) => r.data);

export const adminStats = () =>
  api.get("/admin/stats", { headers: adminHeaders() }).then((r) => r.data);

export const adminAnalytics = (days = 7) =>
  api
    .get("/admin/analytics", { headers: adminHeaders(), params: { days }, timeout: 20000 })
    .then((r) => r.data);

export const adminConciergeStats = () =>
  api.get("/admin/concierge-stats", { headers: adminHeaders() }).then((r) => r.data);

export const adminBadgeEmbeds = () =>
  api.get("/admin/badge-embeds", { headers: adminHeaders() }).then((r) => r.data);

// X (Twitter) outreach — announce newly-listed tools from @terminalvalue_1.
export const adminXStatus = () =>
  api.get("/admin/x-outreach/status", { headers: adminHeaders(), timeout: 15000 }).then((r) => r.data);

// Daily editorial post engine
export const adminXDailyStatus = () =>
  api.get("/admin/x-daily/status", { headers: adminHeaders(), timeout: 15000 }).then((r) => r.data);

export const adminXDailySetSettings = (settings) =>
  api
    .post("/admin/x-daily/settings", settings, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminXDailyEdit = (text) =>
  api
    .put("/admin/x-daily/draft", { text }, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminXDailyPost = () =>
  api
    .post("/admin/x-daily/post", null, { headers: adminHeaders(), timeout: 30000 })
    .then((r) => r.data);

export const adminXDailyRevise = (message) =>
  api
    .post("/admin/x-daily/revise", { message }, { headers: adminHeaders(), timeout: 45000 })
    .then((r) => r.data);

export const adminXDailyGenerate = ({ templateId, postType, listId, excludeSlugs, focusSlug } = {}) =>
  api
    .post("/admin/x-daily/generate", null, {
      headers: adminHeaders(),
      params: {
        force: true,
        ...(templateId ? { template_id: templateId } : {}),
        ...(postType && postType !== "auto" ? { post_type: postType } : {}),
        ...(listId ? { list_id: listId } : {}),
        ...(excludeSlugs && excludeSlugs.length ? { exclude_slugs: excludeSlugs.join(",") } : {}),
        ...(focusSlug ? { focus_slug: focusSlug } : {}),
      },
      timeout: 45000,
    })
    .then((r) => r.data);

export const adminXDailyToolSearch = (q) =>
  api
    .get("/admin/x-daily/tools", { headers: adminHeaders(), params: { q }, timeout: 10000 })
    .then((r) => r.data);

export const adminXDailySaveTemplate = (label, style) =>
  api
    .post("/admin/x-daily/templates", { label, style }, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminXDailyDeleteTemplate = (templateId) =>
  api
    .delete(`/admin/x-daily/templates/${templateId}`, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminXDailyPlaybook = (refresh = false) =>
  api
    .get("/admin/x-daily/playbook", { headers: adminHeaders(), params: { refresh }, timeout: 60000 })
    .then((r) => r.data);

export const adminXSetSettings = (auto_announce) =>
  api
    .post("/admin/x-outreach/settings", { auto_announce }, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminXPreview = (slug) =>
  api
    .get(`/admin/x-outreach/preview/${encodeURIComponent(slug)}`, { headers: adminHeaders(), timeout: 15000 })
    .then((r) => r.data);

export const adminXAnnounceBySlug = (slug, force = false) =>
  api
    .post(`/admin/tools/by-slug/${encodeURIComponent(slug)}/announce-x`, null, {
      headers: adminHeaders(),
      params: force ? { force: true } : {},
      timeout: 30000,
    })
    .then((r) => r.data);

// AI-powered tool auto-add. The backend scrapes the URL + a couple of subpages,
// asks Claude to extract structured fields, saves the result as a pending Tool,
// and then runs the full enrichment draft inline (overview / best-for /
// workflow / user-base / pricing / AI alternatives) reusing the same scrape.
// Long timeout — scrape + multiple LLM round-trips can take 60–120 s.
export const adminAutoAdd = (url) =>
  api
    .post(
      "/admin/auto-add",
      { url },
      { headers: adminHeaders(), timeout: 180000 },
    )
    .then((r) => r.data);

// Multi-turn refinement of an already-scraped tool. Pass the conversation
// history so Claude has context across turns ("also add X", "no make
// pricing FREE-$$ not $$$", etc.) without losing it.
export const adminRefineTool = (toolId, feedback, history) =>
  api
    .post(
      `/admin/tools/${toolId}/refine`,
      { feedback, history: history || [] },
      { headers: adminHeaders(), timeout: 60000 },
    )
    .then((r) => r.data);

// Force-regenerate all per-tool prerendered HTML files. Useful when a
// redeploy skipped the postbuild step or a fresh approval's card is
// rendering as the generic homepage on social shares.
export const adminPrerenderAll = () =>
  api
    .post("/admin/prerender", null, { headers: adminHeaders(), timeout: 60000 })
    .then((r) => r.data);

// One-time, per-environment action: tag well-known multi-function terminals
// (Koyfin, TIKR, Godel, …). Idempotent + non-destructive (skips already-flagged).
export const adminSeedTerminals = () =>
  api
    .post("/admin/seed-terminals", null, { headers: adminHeaders(), timeout: 60000 })
    .then((r) => r.data);

// Diagnostic: read back what the backend has on disk for a given slug.
// Used to confirm whether share-card issues are write failures vs. a
// filesystem-isolation problem we can't fix from code.
export const adminPrerenderInspect = (slug) =>
  api
    .get("/admin/prerender/inspect", {
      params: { slug },
      headers: adminHeaders(),
      timeout: 30000,
    })
    .then((r) => r.data);

// Bulk v2 taxonomy extract — fires a fire-and-forget background task on
// the backend that scrapes every approved tool with Claude and fills in
// `instruments`, `regions`, `audience`, and `pro_segment`. Returns
// immediately. Poll adminBulkExtractV2Status() to watch progress.
//
// `force=true` re-extracts even tools that already have all v2 fields
// populated; default is idempotent (only-missing).
export const adminBulkExtractV2 = ({ force = false, limit = 500 } = {}) =>
  api
    .post(
      "/admin/bulk-extract-v2",
      null,
      {
        params: { force, limit },
        headers: adminHeaders(),
        timeout: 30000,
      },
    )
    .then((r) => r.data);

export const adminBulkExtractV2Status = () =>
  api
    .get("/admin/bulk-extract-v2/status", {
      headers: adminHeaders(),
      timeout: 10000,
    })
    .then((r) => r.data);

// AI enrichment ("site descriptions") — draft → review → approve
export const adminEnrichTool = (toolId) =>
  api
    .post(`/admin/tools/${toolId}/enrich`, null, {
      headers: adminHeaders(),
      timeout: 150000,
    })
    .then((r) => r.data);

export const adminEnrichBulk = ({ force = false, limit = 1000 } = {}) =>
  api
    .post("/admin/enrich-bulk", null, {
      params: { force, limit },
      headers: adminHeaders(),
      timeout: 30000,
    })
    .then((r) => r.data);

export const adminEnrichBulkStatus = () =>
  api
    .get("/admin/enrich-bulk/status", { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminListUsers = () =>
  api
    .get("/admin/users", { headers: adminHeaders(), timeout: 15000 })
    .then((r) => r.data);

export const adminEnrichBulkCancel = () =>
  api
    .post("/admin/enrich-bulk/cancel", null, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

// Backfill social handles (deterministic, no LLM)
export const adminBackfillSocial = ({ onlyMissing = true } = {}) =>
  api
    .post("/admin/backfill-social", null, {
      headers: adminHeaders(),
      params: { only_missing: onlyMissing, limit: 5000 },
      timeout: 15000,
    })
    .then((r) => r.data);

export const adminBackfillSocialStatus = () =>
  api
    .get("/admin/backfill-social/status", { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminBackfillSocialCancel = () =>
  api
    .post("/admin/backfill-social/cancel", null, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminRecomputeAlternatives = () =>
  api
    .post("/admin/recompute-alternatives", null, { headers: adminHeaders(), timeout: 30000 })
    .then((r) => r.data);

export const adminRecomputeAlternativesStatus = () =>
  api
    .get("/admin/recompute-alternatives/status", { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminRecomputeAlternativesCancel = () =>
  api
    .post("/admin/recompute-alternatives/cancel", null, { headers: adminHeaders(), timeout: 10000 })
    .then((r) => r.data);

export const adminEnrichQueue = (status = "draft") =>
  api
    .get("/admin/enrich-queue", { params: { status }, headers: adminHeaders(), timeout: 20000 })
    .then((r) => r.data);

// Pricing freshness — "pricing changed" review queue
export const adminPricingQueue = () =>
  api
    .get("/admin/pricing-queue", { headers: adminHeaders(), timeout: 20000 })
    .then((r) => r.data);

export const adminPricingApprove = (toolId) =>
  api
    .post(`/admin/tools/${toolId}/pricing/approve`, null, { headers: adminHeaders(), timeout: 15000 })
    .then((r) => r.data);

export const adminPricingReject = (toolId) =>
  api
    .post(`/admin/tools/${toolId}/pricing/reject`, null, { headers: adminHeaders(), timeout: 15000 })
    .then((r) => r.data);

export const adminPricingRecheck = (toolId) =>
  api
    .post(`/admin/tools/${toolId}/pricing/recheck`, null, { headers: adminHeaders(), timeout: 150000 })
    .then((r) => r.data);

// Contact / messages
export const sendMessage = (payload) => api.post("/messages", payload).then((r) => r.data);

export const adminListMessages = (status) =>
  api.get("/admin/messages", { params: status ? { status } : {}, headers: adminHeaders() }).then((r) => r.data.messages);

export const adminSetMessageStatus = (id, status) =>
  api.post(`/admin/messages/${id}/status`, { status }, { headers: adminHeaders() }).then((r) => r.data);

export const adminDeleteMessage = (id) =>
  api.delete(`/admin/messages/${id}`, { headers: adminHeaders() }).then((r) => r.data);

// Admin curated lists ("bibles") — terminal-style shareable list images.
export const adminListBibles = () =>
  api.get("/admin/bibles", { headers: adminHeaders() }).then((r) => r.data.bibles);

export const adminCreateBible = (payload) =>
  api.post("/admin/bibles", payload, { headers: adminHeaders() }).then((r) => r.data);

export const adminGetBible = (id) =>
  api.get(`/admin/bibles/${id}`, { headers: adminHeaders() }).then((r) => r.data);

export const adminUpdateBible = (id, payload) =>
  api.put(`/admin/bibles/${id}`, payload, { headers: adminHeaders() }).then((r) => r.data);

export const adminDeleteBible = (id) =>
  api.delete(`/admin/bibles/${id}`, { headers: adminHeaders() }).then((r) => r.data);

// Public read of a saved list — used by the /list/<id> page.
export const fetchPublicBible = (id) =>
  api.get(`/bibles/${id}`).then((r) => r.data);

// Public PNG endpoint; cache-bust param forces social crawlers / preview to refetch.
export const bibleImageUrl = (id, bust) =>
  `${API}/og/bible/${id}.png${bust ? `?t=${bust}` : ""}`;

// Render a draft list to PNG without saving — used in the admin editor for
// instant preview after [ generate ] before persisting.
export const adminBiblePreviewPngUrl = () => `${API}/admin/bibles/preview.png`;


// AI Concierge — Claude-powered tool recommender. Long timeout because
// the LLM round-trip with the full catalog as context can take 5-20s.
export const conciergeAsk = ({ session_id, message, surface = "web" }) =>
  api
    .post(
      "/ask",
      { session_id: session_id || null, message, surface },
      { timeout: 60000 },
    )
    .then((r) => r.data);

export const conciergeGetSession = (sid) =>
  api.get(`/ask/${sid}`, { timeout: 15000 }).then((r) => r.data);

// Ticker/company symbol autocomplete for the terminal ticker picker.
export const searchSymbols = (q, signal) =>
  api
    .get("/symbols/search", { params: { q, limit: 10 }, signal, timeout: 12000 })
    .then((r) => r.data);

// Company profile (name/sector/industry) for a ticker — used to give the AI
// real context so recommendations match the company's vertical.
export const getSymbolProfile = (symbol) =>
  api
    .get("/symbols/profile", { params: { symbol }, timeout: 12000 })
    .then((r) => r.data);

// Streaming concierge — POSTs to /ask/stream and reads the plain-text token
// stream via fetch + ReadableStream (axios can't surface a partial body).
// `onMeta({ session_id, rate_remaining })` fires once headers are available;
// `onChunk(delta, full)` fires per token batch. Resolves with the full text.
export const conciergeAskStream = async (
  { session_id, message, surface = "web" },
  { onMeta, onChunk } = {},
) => {
  const resp = await fetch(`${API}/ask/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: session_id || null, message, surface }),
  });

  if (!resp.ok || !resp.body) {
    let detail = "";
    try {
      detail = (await resp.json())?.detail || "";
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(detail || `HTTP ${resp.status}`);
    err.status = resp.status;
    err.detail = detail;
    throw err;
  }

  if (onMeta) {
    onMeta({
      session_id: resp.headers.get("X-Session-Id"),
      rate_remaining: resp.headers.get("X-Rate-Remaining"),
    });
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let full = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const delta = decoder.decode(value, { stream: true });
    if (delta) {
      full += delta;
      if (onChunk) onChunk(delta, full);
    }
  }
  return full;
};
